import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { streamChat, parseJobDescription, conversationsApi, type Conversation } from '../services/api';
import { jobsService, type Job } from '../services/jobs.service';
import { trainingService } from '../services/training.service';

interface ParsedJob {
  company: string;
  role: string;
  jobUrl: string | null;
  notes: string;
}

interface VideoRecommendation {
  lessonId: string;
  trainingId: string;
  reason: string;
  raw: string;
  resolvedTrainingId?: string;
  lessonTitle?: string;
  trainingTitle?: string;
  thumbnailUrl?: string | null;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  isStreaming?: boolean;
  parsedJob?: ParsedJob;
  jobCreatedId?: string;
  duplicateOf?: Job;
  recommendations?: VideoRecommendation[];
}

interface RedirectedAssistantState {
  initialMessage?: string;
  autoCreateJob?: boolean;
  skipJobParsing?: boolean;
  chatContext?: 'general' | 'algo';
  activeJobContext?: {
    id: string;
    company: string;
    role: string;
  };
}

const BENTO_ACTIONS = [
  {
    icon: 'work',
    color: 'text-primary',
    title: 'Analyze a Job Post',
    description: 'Paste a job description and get a fit analysis, skill gaps, and red flags.',
    prompt: "I'm going to paste a job description. Analyze it and tell me: 1) How well my profile matches the requirements, 2) What skills I should emphasize in my application, and 3) Any red flags or questions I should ask the recruiter.",
  },
  {
    icon: 'payments',
    color: 'text-secondary',
    title: 'Negotiate an Offer',
    description: 'Get a strategy and exact scripts to negotiate your salary with confidence.',
    prompt: 'Help me negotiate a job offer. Ask me about the role, my current compensation, and the offer details. Then give me a step-by-step negotiation strategy and exact word-for-word scripts I can use with the recruiter.',
  },
  {
    icon: 'psychology',
    color: 'text-tertiary',
    title: 'Skill Gap Analysis',
    description: 'Find what you are missing for your target role and get a 3-month roadmap.',
    prompt: 'I want to land a [target role] position. Analyze my current profile, identify the biggest skill gaps between where I am and where I need to be, and give me a 3-month prioritized learning roadmap with specific projects and resources.',
  },
];

function FormattedContent({ content }: { content: string }) {
  return (
    <ReactMarkdown
      components={{
        p: ({ children }) => <p className="text-sm mb-1 last:mb-0">{children}</p>,
        strong: ({ children }) => <strong className="font-semibold text-on-surface">{children}</strong>,
        ul: ({ children }) => <ul className="list-disc list-inside space-y-1 my-1">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 my-1">{children}</ol>,
        li: ({ children }) => <li className="text-sm marker:text-primary">{children}</li>,
        h1: ({ children }) => <h1 className="text-base font-bold text-on-surface mt-3 mb-1">{children}</h1>,
        h2: ({ children }) => <h2 className="text-sm font-bold text-on-surface mt-3 mb-1">{children}</h2>,
        h3: ({ children }) => <h3 className="text-sm font-semibold text-on-surface mt-2 mb-1">{children}</h3>,
        code: ({ className, children, ...props }) => {
          const isBlock = Boolean(className);
          if (isBlock) {
            const lang = (className ?? '').replace('language-', '');
            return (
              <div className="my-2 rounded-lg overflow-hidden border border-outline-variant/10">
                {lang && (
                  <div className="px-3 py-1 bg-surface-container text-[10px] font-mono text-on-surface-variant uppercase tracking-widest border-b border-outline-variant/10">
                    {lang}
                  </div>
                )}
                <pre className="overflow-x-auto bg-surface-container-lowest p-4 text-xs font-mono leading-relaxed text-on-surface">
                  <code>{children}</code>
                </pre>
              </div>
            );
          }
          return (
            <code className="bg-surface-container-high px-1 py-0.5 rounded text-primary text-xs font-mono" {...props}>
              {children}
            </code>
          );
        },
        pre: ({ children }) => <>{children}</>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

function ParsedJobCard({
  message,
  onAdd,
  onDismiss,
}: {
  message: Message;
  onAdd: (msgId: string, job: ParsedJob) => void;
  onDismiss: (msgId: string) => void;
}) {
  const navigate = useNavigate();
  const job = message.parsedJob!;

  if (message.duplicateOf && !message.jobCreatedId) {
    const dupe = message.duplicateOf;
    const statusLabel: Record<string, string> = {
      APPLIED: 'Contacted', HR: 'HR Interview', TECH: 'Next Instances',
      OFFER: 'Offer', REJECTED: 'Rejected',
    };
    return (
      <div className="flex gap-2 md:gap-4 max-w-3xl">
        <div className="shrink-0">
          <div className="w-8 h-8 rounded-full bg-surface-container-high flex items-center justify-center">
            <span className="material-symbols-outlined text-on-surface-variant text-sm">warning</span>
          </div>
        </div>
        <div className="bg-surface-container border border-outline-variant/10 rounded-xl overflow-hidden flex-1 min-w-0">
          <div className="px-5 py-4 border-b border-outline-variant/10">
            <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-3">
              Duplicate Application Detected
            </p>
            <p className="text-sm text-on-surface-variant mb-3">
              You already have an application for this position:
            </p>
            <div className="bg-surface-container-high rounded-lg p-3 border border-outline-variant/10">
              <p className="font-semibold text-sm text-on-surface">{dupe.company}</p>
              <p className="text-xs text-on-surface-variant mt-0.5">{dupe.role}</p>
              <p className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant/60 mt-2">
                {statusLabel[dupe.status] ?? dupe.status}
              </p>
            </div>
          </div>
          <div className="px-5 py-3 flex items-center gap-3">
            <button
              onClick={() => onAdd(message.id, job)}
              className="flex items-center gap-1.5 px-4 py-2 bg-surface-container-high text-on-surface rounded-lg text-xs font-bold transition-colors hover:bg-surface-bright"
            >
              Add Anyway
            </button>
            <button
              onClick={() => onDismiss(message.id)}
              className="text-xs text-on-surface-variant hover:text-on-surface transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (message.jobCreatedId) {
    return (
      <div className="flex gap-2 md:gap-4 max-w-3xl">
        <div className="shrink-0">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="material-symbols-outlined text-primary text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>
              check_circle
            </span>
          </div>
        </div>
        <div className="bg-surface-container border border-primary/20 px-5 py-4 rounded-xl rounded-tl-none text-sm space-y-2">
          <p className="font-semibold text-primary flex items-center gap-2">
            <span className="material-symbols-outlined text-base">ads_click</span>
            Application added to Job Tracker
          </p>
          <p className="text-on-surface-variant text-xs">{job.role} at {job.company}</p>
          <button
            onClick={() => navigate(`/jobs/${message.jobCreatedId}`)}
            className="text-xs font-bold text-primary hover:underline flex items-center gap-1 mt-1"
          >
            View application <span className="material-symbols-outlined text-xs">arrow_forward</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2 md:gap-4 max-w-3xl">
      <div className="shrink-0">
        <div className="w-8 h-8 rounded-full primary-gradient flex items-center justify-center">
          <span className="material-symbols-outlined text-on-primary text-xs" style={{ fontVariationSettings: "'FILL' 1" }}>
            auto_awesome
          </span>
        </div>
      </div>
      <div className="space-y-3 flex-1 min-w-0">
        <p className="text-xs font-bold text-on-surface-variant uppercase tracking-widest">
          Job Description Detected
        </p>
        <div className="bg-surface-container border border-outline-variant/10 rounded-xl overflow-hidden">
          {/* Header */}
          <div className="px-5 py-4 border-b border-outline-variant/10 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-surface-container-highest flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-primary">work</span>
            </div>
            <div>
              <p className="font-semibold text-on-surface text-sm">{job.role}</p>
              <p className="text-on-surface-variant text-xs">{job.company}</p>
            </div>
            {job.jobUrl && (
              <a
                href={job.jobUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto text-xs text-primary hover:underline flex items-center gap-1"
              >
                <span className="material-symbols-outlined text-xs">open_in_new</span>
                View posting
              </a>
            )}
          </div>
          {/* Notes */}
          {job.notes && (
            <div className="px-5 py-3 bg-surface-container-lowest/40">
              <p className="text-xs text-on-surface-variant leading-relaxed">{job.notes}</p>
            </div>
          )}
          {/* Actions */}
          <div className="px-5 py-3 flex items-center gap-3 border-t border-outline-variant/10">
            <button
              onClick={() => onAdd(message.id, job)}
              className="flex items-center gap-1.5 px-4 py-2 primary-gradient text-on-primary rounded-lg text-xs font-bold transition-opacity hover:opacity-90"
            >
              <span className="material-symbols-outlined text-sm">add</span>
              Add to Job Tracker
            </button>
            <button
              onClick={() => onDismiss(message.id)}
              className="text-xs text-on-surface-variant hover:text-on-surface transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VideoRecommendationCard({ rec }: { rec: VideoRecommendation }) {
  const navigate = useNavigate();
  const handleWatch = async () => {
    const targetTrainingId = rec.resolvedTrainingId || rec.trainingId;
    navigate(`/training/modules/${targetTrainingId}/lessons/${rec.lessonId}`);
  };
  return (
    <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mt-2">
      <div className="flex items-start gap-3">
        {rec.thumbnailUrl ? (
          <img
            src={rec.thumbnailUrl}
            alt={rec.lessonTitle || rec.trainingTitle || 'Recommended video'}
            className="h-16 w-28 shrink-0 rounded-xl object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-10 h-10 rounded-xl primary-gradient flex items-center justify-center shrink-0">
            <span className="material-symbols-outlined text-on-primary text-lg">play_circle</span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-primary uppercase tracking-widest mb-1">Featured Video</p>
          {rec.lessonTitle ? <p className="text-sm font-semibold text-on-surface line-clamp-2">{rec.lessonTitle}</p> : null}
          {rec.trainingTitle ? <p className="mt-0.5 text-xs text-on-surface-variant">{rec.trainingTitle}</p> : null}
          <p className="text-sm text-on-surface-variant leading-relaxed">{rec.reason}</p>
          <button
            onClick={handleWatch}
            className="mt-2 flex items-center gap-1 text-xs font-bold text-primary hover:underline"
          >
            Watch now <span className="material-symbols-outlined text-xs">arrow_forward</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function parseVideoRecommendations(text: string): { text: string; recommendations: VideoRecommendation[] } {
  const recommendations: VideoRecommendation[] = [];
  const regex = /\[VIDEO:([^|]+)\|([^|]+)\|([^\]]+)\]/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    recommendations.push({
      lessonId: match[1].trim(),
      trainingId: match[2].trim(),
      reason: match[3].trim(),
      raw: match[0],
    });
  }
  const cleanedText = text.replace(regex, '').trim();
  return { text: cleanedText, recommendations };
}

function MessageBubble({ message, onAdd, onDismiss }: {
  message: Message;
  onAdd: (msgId: string, job: ParsedJob) => void;
  onDismiss: (msgId: string) => void;
}) {
  const [copied, setCopied] = useState(false);

  if (message.role === 'tool') {
    return <ParsedJobCard message={message} onAdd={onAdd} onDismiss={onDismiss} />;
  }

  const isUser = message.role === 'user';

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (isUser) {
    return (
      <div className="flex gap-2 md:gap-4 max-w-3xl ml-auto flex-row-reverse">
        <div className="shrink-0">
          <div className="w-8 h-8 rounded-full bg-surface-container-highest flex items-center justify-center">
            <span className="material-symbols-outlined text-sm text-on-surface-variant">person</span>
          </div>
        </div>
        <div className="bg-surface-container-high px-4 py-3 rounded-xl rounded-tr-none text-on-surface text-sm leading-relaxed max-w-full whitespace-pre-wrap break-words">
          {message.content.length > 300 ? message.content.slice(0, 300) + '…' : message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2 md:gap-4 max-w-3xl">
      <div className="shrink-0">
        <div className="w-8 h-8 rounded-full primary-gradient flex items-center justify-center">
          <span className="material-symbols-outlined text-on-primary text-xs" style={{ fontVariationSettings: "'FILL' 1" }}>
            auto_awesome
          </span>
        </div>
      </div>
      <div className="space-y-1 min-w-0 flex-1">
        <div className="bg-surface-container px-5 py-4 rounded-xl rounded-tl-none text-on-surface text-sm leading-relaxed border border-outline-variant/5">
          {message.content ? (
            <FormattedContent content={message.content} />
          ) : message.isStreaming ? (
            <span className="inline-flex gap-1">
              <span className="w-1.5 h-1.5 bg-on-surface-variant rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 bg-on-surface-variant rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 bg-on-surface-variant rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </span>
          ) : null}
        </div>
        {message.recommendations?.map((rec) => (
          <VideoRecommendationCard key={rec.lessonId} rec={rec} />
        ))}
        {message.content && !message.isStreaming && (
          <div className="flex gap-2 pt-1">
            <button className="text-[10px] uppercase tracking-wider font-bold text-on-surface-variant hover:text-primary flex items-center gap-1 transition-colors">
              <span className="material-symbols-outlined text-xs">thumb_up</span> Helpful
            </button>
            <button className="text-[10px] uppercase tracking-wider font-bold text-on-surface-variant hover:text-error flex items-center gap-1 transition-colors">
              <span className="material-symbols-outlined text-xs">thumb_down</span>
            </button>
            <button
              onClick={handleCopy}
              className="text-[10px] uppercase tracking-wider font-bold text-on-surface-variant hover:text-on-surface flex items-center gap-1 transition-colors ml-2"
            >
              <span className="material-symbols-outlined text-xs">{copied ? 'check' : 'content_copy'}</span>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function formatRelativeDate(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function AiAssistant() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeJobContext, setActiveJobContext] = useState<RedirectedAssistantState['activeJobContext'] | null>(null);
  const chatContextRef = useRef<'general' | 'algo'>('general');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const redirectedMessageRef = useRef<string | null>(null);
  const recommendationMetaCacheRef = useRef(new Map<string, VideoRecommendation>());
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { data: conversations = [] } = useQuery<Conversation[]>({
    queryKey: ['conversations'],
    queryFn: () => conversationsApi.list(),
  });

  const { data: existingJobs = [] } = useQuery<Job[]>({
    queryKey: ['jobs'],
    queryFn: () => jobsService.findAll(),
  });

  useEffect(() => {
    if (messages.length === 0) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  useEffect(() => {
    const pending = messages
      .flatMap((msg) => msg.recommendations ?? [])
      .filter((rec) => !rec.lessonTitle);

    if (pending.length === 0) return;

    const uniqueLessonIds = Array.from(new Set(pending.map((rec) => rec.lessonId)));
    let cancelled = false;

    async function hydrateRecommendation(rec: VideoRecommendation) {
      const cached = recommendationMetaCacheRef.current.get(rec.lessonId);
      if (cached) return cached;

      const { trainingId: resolvedTrainingId } = await trainingService.resolveLesson(rec.lessonId);
      const training = await trainingService.findOne(resolvedTrainingId);
      const lesson = training.lessons.find((item) => item.id === rec.lessonId);

      const hydrated: VideoRecommendation = {
        ...rec,
        trainingId: resolvedTrainingId,
        resolvedTrainingId,
        lessonTitle: lesson?.title ?? rec.lessonId,
        trainingTitle: training.title,
        thumbnailUrl: training.thumbnailPresignedUrl || training.thumbnailUrl || null,
      };

      recommendationMetaCacheRef.current.set(rec.lessonId, hydrated);
      return hydrated;
    }

    Promise.all(
      uniqueLessonIds.map(async (lessonId) => {
        const source = pending.find((rec) => rec.lessonId === lessonId);
        if (!source) return null;
        try {
          return await hydrateRecommendation(source);
        } catch {
          return null;
        }
      }),
    ).then((hydrated) => {
      if (cancelled) return;

      const byLessonId = new Map(hydrated.filter(Boolean).map((rec) => [rec!.lessonId, rec!]));
      if (byLessonId.size === 0) return;

      setMessages((prev) =>
        prev.map((msg) => {
          if (!msg.recommendations?.length) return msg;
          return {
            ...msg,
            recommendations: msg.recommendations.map((rec) => byLessonId.get(rec.lessonId) ?? rec),
          };
        }),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [messages]);

  const loadConversation = useCallback(async (id: string) => {
    const convo = await conversationsApi.get(id);
    setConversationId(convo.id);
    setMessages(
      convo.messages.map((m) => {
        if (m.role === 'assistant') {
          const parsed = parseVideoRecommendations(m.content);
          return { id: m.id, role: m.role, content: parsed.text, recommendations: parsed.recommendations };
        }
        return { id: m.id, role: m.role, content: m.content };
      }),
    );
  }, []);

  const startNewChat = useCallback(() => {
    setConversationId(undefined);
    setMessages([]);
    setInput('');
  }, []);

  const sendMessage = useCallback(
    async (text?: string, options?: { autoCreateJob?: boolean; skipJobParsing?: boolean; chatContext?: 'general' | 'algo' }) => {
      const content = (text ?? input).trim();
      if (!content || isStreaming) return;

      const userMsg: Message = { id: crypto.randomUUID(), role: 'user', content };
      const assistantMsgId = crypto.randomUUID();
      const assistantMsg: Message = { id: assistantMsgId, role: 'assistant', content: '', isStreaming: true };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setInput('');
      setIsStreaming(true);

      const history = messages
        .filter((m) => m.role !== 'tool')
        .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));

      // Auto-parse as JD in parallel if message is long enough (skip for non-JD contexts)
      if (content.length > 150 && !options?.skipJobParsing) {
        setIsParsing(true);
        parseJobDescription(content)
          .then(async (parsed) => {
            if (options?.autoCreateJob) {
              const norm = (s: string) => s.trim().toLowerCase();
              const duplicate = existingJobs.find(
                (j) =>
                  norm(j.company) === norm(parsed.company) &&
                  norm(j.role) === norm(parsed.role),
              );
              if (duplicate) {
                setMessages((prev) => [
                  ...prev,
                  {
                    id: crypto.randomUUID(),
                    role: 'tool',
                    content: '',
                    parsedJob: parsed,
                    duplicateOf: duplicate,
                  },
                ]);
                return;
              }
              try {
                const created = await jobsService.create({
                  company: parsed.company,
                  role: parsed.role,
                  notes: parsed.notes,
                  jobUrl: parsed.jobUrl ?? undefined,
                  status: 'APPLIED',
                });
                setMessages((prev) => [
                  ...prev,
                  {
                    id: crypto.randomUUID(),
                    role: 'tool',
                    content: '',
                    parsedJob: parsed,
                    jobCreatedId: created.id,
                  },
                ]);
                queryClient.invalidateQueries({ queryKey: ['jobs'] });
                queryClient.invalidateQueries({ queryKey: ['job-stats'] });
                return;
              } catch {
                // Fall back to the manual confirmation card if ticket creation fails.
              }
            }

            setMessages((prev) => [
              ...prev,
              {
                id: crypto.randomUUID(),
                role: 'tool',
                content: '',
                parsedJob: parsed,
              },
            ]);
          })
          .catch(() => { /* silently skip if not a JD */ })
          .finally(() => setIsParsing(false));
      }

      await streamChat(
        content,
        history,
        (chunk) => {
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantMsgId ? { ...m, content: m.content + chunk } : m)),
          );
        },
        () => {
          setMessages((prev) => {
            const updated = prev.map((m) => {
              if (m.id !== assistantMsgId) return m;
              const parsed = parseVideoRecommendations(m.content);
              return { ...m, content: parsed.text, isStreaming: false, recommendations: parsed.recommendations };
            });
            return updated;
          });
          setIsStreaming(false);
          queryClient.invalidateQueries({ queryKey: ['conversations'] });
        },
        (err) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsgId ? { ...m, content: `Error: ${err}`, isStreaming: false } : m,
            ),
          );
          setIsStreaming(false);
        },
        conversationId,
        (id) => setConversationId(id),
        activeJobContext?.id,
        options?.chatContext ?? chatContextRef.current,
      );
    },
    [input, isStreaming, messages, conversationId, queryClient, activeJobContext?.id, existingJobs],
  );

  useEffect(() => {
    const state = location.state as RedirectedAssistantState | null;
    const redirectedMessage = state?.initialMessage?.trim();
    if (state?.activeJobContext) {
      setActiveJobContext(state.activeJobContext);
    }
    if (state?.chatContext) {
      chatContextRef.current = state.chatContext;
    }
    if (!redirectedMessage || isStreaming) return;

    const dedupeKey = `${redirectedMessage}:${state?.autoCreateJob ? 'auto' : 'manual'}`;
    if (redirectedMessageRef.current === dedupeKey) return;

    redirectedMessageRef.current = dedupeKey;
    navigate(location.pathname, { replace: true, state: null });
    void sendMessage(redirectedMessage, {
      autoCreateJob: Boolean(state?.autoCreateJob),
      skipJobParsing: Boolean(state?.skipJobParsing),
      chatContext: state?.chatContext,
    });
  }, [location.pathname, location.state, navigate, isStreaming, sendMessage]);

  const handleAddToTracker = useCallback(async (msgId: string, job: ParsedJob) => {
    try {
      const created = await jobsService.create({
        company: job.company,
        role: job.role,
        notes: job.notes,
        jobUrl: job.jobUrl ?? undefined,
        status: 'APPLIED',
      });
      setMessages((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, jobCreatedId: created.id } : m)),
      );
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job-stats'] });
    } catch {
      // silently fail — user can try again
    }
  }, [queryClient]);

  const handleDismiss = useCallback((msgId: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== msgId));
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const isWelcome = messages.length === 0;

  return (
    <div className="flex h-[calc(100svh-3.5rem)] md:h-screen bg-surface overflow-hidden relative">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="md:hidden absolute inset-0 z-20 bg-black/40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Conversations sidebar */}
      <aside
        className={`absolute md:relative inset-y-0 left-0 z-30 md:z-auto ${sidebarOpen ? 'w-64' : 'w-0'} transition-all duration-300 overflow-hidden shrink-0 border-r border-outline-variant/10 flex flex-col bg-surface-container-low`}
      >
        {/* Sidebar header */}
        <div className="p-4 flex items-center justify-between shrink-0">
          <span className="text-xs font-bold uppercase tracking-widest text-on-surface-variant/60">History</span>
          <button
            onClick={startNewChat}
            className="flex items-center gap-1 px-3 py-1.5 primary-gradient text-on-primary rounded-lg text-xs font-bold hover:opacity-90 transition-opacity"
          >
            <span className="material-symbols-outlined text-sm">add</span>
            New
          </button>
        </div>

        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-1">
          {conversations.length === 0 ? (
            <p className="text-xs text-on-surface-variant/40 text-center py-8 px-4">No conversations yet</p>
          ) : (
            conversations.map((convo) => (
              <button
                key={convo.id}
                onClick={() => loadConversation(convo.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors group ${
                  conversationId === convo.id
                    ? 'bg-primary/10 text-on-surface'
                    : 'hover:bg-surface-container text-on-surface-variant hover:text-on-surface'
                }`}
              >
                <p className="text-xs font-medium truncate">{convo.title}</p>
                <p className="text-[10px] text-on-surface-variant/50 mt-0.5">{formatRelativeDate(convo.updatedAt)}</p>
              </button>
            ))
          )}
        </div>
      </aside>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col relative overflow-hidden">
        {/* Ambient background glows */}
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-tertiary/5 rounded-full blur-[100px] pointer-events-none" />

        <button
          onClick={() => setSidebarOpen((v) => !v)}
          className="md:hidden absolute top-3 left-3 z-20 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-outline-variant/10 bg-surface-container/90 text-on-surface-variant shadow-sm backdrop-blur transition-colors hover:bg-surface-container-high hover:text-on-surface"
          aria-label="Open history"
        >
          <span className="flex h-5 w-5 items-center justify-center">
            <span className="material-symbols-outlined block text-xl leading-none">history</span>
          </span>
        </button>

        {/* Top bar */}
        <div className="hidden md:flex shrink-0 items-center gap-3 px-6 py-3 border-b border-outline-variant/10 relative z-10">
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-on-surface-variant hover:text-on-surface hover:bg-surface-container transition-colors"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <span className="material-symbols-outlined block text-xl leading-none">history</span>
            </span>
          </button>
          <span className="text-sm font-semibold text-on-surface truncate">
            {conversationId
              ? (conversations.find((c) => c.id === conversationId)?.title ?? 'Conversation')
              : 'AI Chat'}
          </span>
          {conversationId && (
            <button
              onClick={startNewChat}
              className="ml-auto flex items-center gap-1 text-xs text-on-surface-variant hover:text-on-surface transition-colors"
            >
              <span className="material-symbols-outlined text-sm">add</span>
              New chat
            </button>
          )}
        </div>

        {/* Chat history */}
        <section className={`flex-1 min-h-0 relative z-10 ${isWelcome ? 'overflow-y-hidden' : 'overflow-y-auto'}`}>
          <div
            className={`flex flex-col px-4 sm:px-8 md:px-12 pt-16 md:pt-6 max-w-5xl mx-auto w-full ${
              isWelcome ? 'h-full justify-center gap-4 md:gap-6 pb-4 md:pb-6' : 'min-h-full justify-end gap-4 md:gap-6 pb-4'
            }`}
          >
            {/* Welcome state */}
            {isWelcome && (
              <div className="flex flex-col items-center justify-center text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl bg-surface-container-high flex items-center justify-center mb-4 md:mb-6 border border-outline-variant/10">
                  <span className="material-symbols-outlined text-primary text-2xl md:text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                    auto_awesome
                  </span>
                </div>
                <h2 className="text-xl md:text-3xl font-semibold tracking-tight text-on-surface mb-2 px-2">
                  How can I accelerate your career today?
                </h2>
                <p className="text-sm md:text-base text-on-surface-variant max-w-sm md:max-w-md mx-auto px-2">
                  I'm your AI Chat, specialized in remote job hunting, technical CV optimization, and interview preparation.
                </p>
              </div>
            )}

            {/* Messages */}
            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                onAdd={handleAddToTracker}
                onDismiss={handleDismiss}
              />
            ))}

            {/* Bento action cards */}
            {isWelcome && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {BENTO_ACTIONS.map((action) => (
                  <button
                    key={action.title}
                    onClick={() => sendMessage(action.prompt)}
                    className="group p-4 sm:p-5 bg-surface-container-low hover:bg-surface-container-high transition-all rounded-xl border border-outline-variant/10 text-left flex sm:block items-center gap-3 sm:gap-0"
                  >
                    <span className={`material-symbols-outlined ${action.color} sm:mb-3 block group-hover:scale-110 transition-transform shrink-0`}>
                      {action.icon}
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-on-surface mb-0.5 sm:mb-1">{action.title}</h3>
                      <p className="text-xs text-on-surface-variant leading-tight">{action.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </section>

        {/* Input footer */}
        <footer data-tour="tour-chat-input" className="px-3 pb-3 pt-2 md:px-8 md:pb-8 md:pt-4 relative z-20 shrink-0">
          <div className="max-w-4xl mx-auto">
            {/* Glass input container */}
            <div className="glass-panel border border-outline-variant/20 rounded-2xl px-3 py-3 md:px-4 md:py-4 shadow-2xl">
              <div className="flex items-center gap-3">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Message AI Chat... or paste a job description"
                  rows={1}
                  disabled={isStreaming || isParsing}
                  className="flex-1 min-w-0 bg-transparent border-none focus:ring-0 text-sm text-on-surface placeholder:text-outline-variant min-h-[48px] px-1 py-3 resize-none outline-none leading-6 max-h-32 whitespace-nowrap overflow-x-auto overflow-y-hidden"
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || isStreaming || isParsing}
                  className="w-12 h-12 shrink-0 self-center rounded-2xl primary-gradient text-on-primary flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-opacity hover:opacity-90"
                  aria-label="Send message"
                >
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                    arrow_upward
                  </span>
                </button>
              </div>
              {/* Meta row */}
              <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-outline-variant/5 px-1 pt-3">
                <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
                  <span className="inline-flex items-center gap-1 rounded-full border border-outline-variant/10 bg-surface-container-low px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/60 shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> System Ready
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-outline-variant/10 bg-surface-container-low px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant/60 shrink-0">
                    <span className="material-symbols-outlined text-xs">history</span>
                    {messages.filter((m) => m.role === 'user').length} msgs
                  </span>
                  {activeJobContext && (
                    <span className="flex min-w-0 items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
                      <span className="material-symbols-outlined text-xs shrink-0">work</span>
                      <span className="truncate">
                        {activeJobContext.company} · {activeJobContext.role}
                      </span>
                      <button
                        onClick={() => setActiveJobContext(null)}
                        className="ml-1 inline-flex shrink-0 text-primary/80 transition-colors hover:text-primary"
                        aria-label="Clear active job context"
                      >
                        <span className="material-symbols-outlined text-xs">close</span>
                      </button>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
